import { classNames } from "primereact/utils";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  GoogleMeetViewState,
  useGoogleMeetSection,
} from "../hooks/useGoogleMeetSection";
import styles from "./GoogleMeetSection.module.scss";

interface IGoogleMeetSectionProps {
  // A profile a global admin opened: the API refuses to tie a Google account to the owner from it.
  isOpenedProfile?: boolean;
}

export const GoogleMeetSection = ({ isOpenedProfile = false }: IGoogleMeetSectionProps) => {
  const {
    viewState,
    googleEmail,
    canDisconnect,
    isLoading,
    isConnecting,
    isDisconnecting,
    handleConnect,
    handleDisconnect,
    refetchStatus,
  } = useGoogleMeetSection();

  const isConnected = viewState === "connected";
  const isBusy = isConnecting || isDisconnecting;
  const showEmail = (isConnected || viewState === "needsReconnect") && !!googleEmail;

  return (
    <div className={styles.root}>
      <div
        className={classNames(styles.card, {
          [styles.card_connected]: isConnected,
          [styles.card_warning]: viewState === "needsReconnect",
        })}
      >
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.iconTile} aria-hidden="true">
              <Icon type={IconType.Google} width={22} height={22} />
            </div>
            <div className={styles.titleText}>
              <span className={styles.cardTitle}>Google акаунт</span>
              <span className={styles.cardSubtitle}>
                Използва се за интеграции с Google. Сега: Meet линкове за дистанционните срещи
              </span>
              {showEmail && (
                <span className={styles.accountEmail}>{googleEmail}</span>
              )}
            </div>
          </div>
          <StatusBadge viewState={viewState} isLoading={isLoading} />
        </div>

        {viewState === "notConnected" && (
          <p className={styles.hint}>
            Свържете Google акаунта си и Planny ще създава Meet линк за всяка
            дистанционна среща и ще го изпраща на клиента.
          </p>
        )}

        {viewState === "needsReconnect" && (
          <p className={styles.hint}>
            Връзката с Google акаунта е прекъсната. Свържете го отново, за да
            продължат да се създават Meet линкове.
          </p>
        )}

        {viewState === "unavailable" && (
          <p className={styles.note}>Временно недостъпно</p>
        )}

        {viewState === "error" && (
          <p className={styles.note}>Състоянието не можа да се зареди.</p>
        )}

        {isOpenedProfile && viewState !== "unavailable" && viewState !== "error" && (
          <p className={styles.note}>
            Google акаунтът се свързва само от собственика, не от профил, отворен от админ панела.
          </p>
        )}

        {!isOpenedProfile && (
          <div className={styles.actions}>
            {viewState === "error" && (
              <DefaultButton
                label="Опитай отново"
                onClick={() => refetchStatus()}
                design="neutral"
                disabled={isLoading}
                width="100%"
              />
            )}
            {viewState === "notConnected" && (
              <DefaultButton
                label={isConnecting ? "Зареждане..." : "Свържи Google"}
                onClick={handleConnect}
                design="accent"
                disabled={isBusy || isLoading}
                width="100%"
              />
            )}
            {viewState === "needsReconnect" && (
              <DefaultButton
                label={isConnecting ? "Зареждане..." : "Свържи отново"}
                onClick={handleConnect}
                design="accent"
                disabled={isBusy || isLoading}
                width="100%"
              />
            )}
            {(isConnected || viewState === "needsReconnect") && canDisconnect && (
              <DefaultButton
                label="Прекъсни връзката"
                onClick={handleDisconnect}
                design="neutral"
                disabled={isBusy || isLoading}
                width="100%"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({
  viewState,
  isLoading,
}: {
  viewState: GoogleMeetViewState;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <span className={classNames(styles.badge, styles.badge_loading)}>
        <span className={styles.badge_dot} />
        Зареждане...
      </span>
    );
  }
  if (viewState === "connected") {
    return (
      <span className={classNames(styles.badge, styles.badge_connected)}>
        <span className={styles.badge_dot} />
        Свързан
      </span>
    );
  }
  if (viewState === "needsReconnect") {
    return (
      <span className={classNames(styles.badge, styles.badge_warning)}>
        <span className={styles.badge_dot} />
        Нужно е повторно свързване
      </span>
    );
  }
  if (viewState === "notConnected") {
    return (
      <span className={classNames(styles.badge, styles.badge_disconnected)}>
        <span className={styles.badge_dot} />
        Не е свързан
      </span>
    );
  }
  return null;
};
